import { z } from "zod";

export const USER_TYPE_OPTIONS = ["Student", "Parent", "School"] as const;

export interface GetInTouchValidationMessages {
  nameRequired?: string;
  nameMinLength?: string;
  nameMaxLength?: string;
  nameLettersOnly?: string;
  phoneRequired?: string;
  phoneLength?: string;
  phoneNumeric?: string;
  userTypeRequired?: string;
  userTypeInvalid?: string;
  schoolNameRequired?: string;
  schoolNameMinLength?: string;
  schoolNameMaxLength?: string;
  cityRequired?: string;
  cityMinLength?: string;
  cityMaxLength?: string;
  cityLettersOnly?: string;
  stateRequired?: string;
  stateMinLength?: string;
  stateMaxLength?: string;
  stateLettersOnly?: string;
}

const defaultValidation: Required<GetInTouchValidationMessages> = {
  nameRequired: "Name is required",
  nameMinLength: "Name must be at least 2 characters",
  nameMaxLength: "Name is too long",
  nameLettersOnly: "Name must contain only letters",
  phoneRequired: "Phone number is required",
  phoneLength: "Phone number must be 10 digits",
  phoneNumeric: "Phone number must contain only numbers",
  userTypeRequired: "Please select who you are",
  userTypeInvalid: "Please select a valid option",
  schoolNameRequired: "School name is required",
  schoolNameMinLength: "School name must be at least 2 characters",
  schoolNameMaxLength: "School name is too long",
  cityRequired: "City is required",
  cityMinLength: "City must be at least 2 characters",
  cityMaxLength: "City is too long",
  cityLettersOnly: "City must contain only letters",
  stateRequired: "State is required",
  stateMinLength: "State must be at least 2 characters",
  stateMaxLength: "State is too long",
  stateLettersOnly: "State must contain only letters",
};

export function createGetInTouchSchema(
  messages: GetInTouchValidationMessages = {},
  userTypeOptions: readonly string[] = USER_TYPE_OPTIONS
) {
  const v = { ...defaultValidation, ...messages };

  return z.object({
    name: z
      .string()
      .min(1, v.nameRequired)
      .min(2, v.nameMinLength)
      .max(100, v.nameMaxLength)
      .regex(/^[a-zA-Z\s]+$/, v.nameLettersOnly),
    phone: z
      .string()
      .min(1, v.phoneRequired)
      .min(10, v.phoneLength)
      .max(10, v.phoneLength)
      .regex(/^\d+$/, v.phoneNumeric),
    userType: z
      .string()
      .min(1, v.userTypeRequired)
      .refine((val) => userTypeOptions.includes(val), {
        message: v.userTypeInvalid,
      }),
    schoolName: z
      .string()
      .min(1, v.schoolNameRequired)
      .min(2, v.schoolNameMinLength)
      .max(200, v.schoolNameMaxLength),
    city: z
      .string()
      .min(1, v.cityRequired)
      .min(2, v.cityMinLength)
      .max(100, v.cityMaxLength)
      .regex(/^[a-zA-Z\s]+$/, v.cityLettersOnly),
    state: z
      .string()
      .min(1, v.stateRequired)
      .min(2, v.stateMinLength)
      .max(100, v.stateMaxLength)
      .regex(/^[a-zA-Z\s]+$/, v.stateLettersOnly),
  });
}

export const getInTouchSchema = createGetInTouchSchema();

export type GetInTouchFormData = z.infer<ReturnType<typeof createGetInTouchSchema>>;

export const validateGetInTouchForm = (data: unknown) => {
  return getInTouchSchema.safeParse(data);
};
